import { produce } from 'immer'
import { enableMapSet } from 'immer'
import { create } from 'zustand'

import { useAlertUIStore } from 'features/alerts/store'
import { useAskYogiUIStore } from 'features/ask-yogi/store'
import { useChartsV2Store } from 'features/charts-v2/store'
import { useChartStore } from 'features/charts/store'
import { useFeedbackControlsStore } from 'features/feedback/store'
import { useFeedbackQueryStore } from 'features/feedback/yogiql-store'
import { useFilterStore, useFilterUIStore } from 'features/filters/store'
import { useKeywordsControlsStore } from 'features/keywords/store'
import { useKeywordsQueryStore } from 'features/keywords/yogiql-store'
import { usePanelStore } from 'features/panels/store'
import { useSuggestedViewsUIStore } from 'features/suggested-views/store'
import { useSummaryUIStore } from 'features/summary/store'
import { DateRange, ProjectDetails, ProjectState } from 'types/project'

enableMapSet()

const clientSettingsDefault = {
  project: {
    toolbar: {
      alignment: null,
    },
    pages: {
      alerts: { alias: '', hidden: false, loading: { text: '' } },
      compass: { alias: '', hidden: false, loading: { text: '' } },
      insights: { alias: '', hidden: false, loading: { text: '' } },
      feedback: { alias: '', hidden: false, loading: { text: '' } },
      network: { alias: '', hidden: false, nodes_limit: 0 },
      dashboard: { alias: '', hidden: false, new_dashboard_button: false },
      sources: { alias: '', hidden: false },
      keywords: { alias: '', hidden: false },
      saved_views: { alias: '', hidden: false },
      products: { alias: '', hidden: false },
      recommendations: { alias: '', hidden: false },
      settings: { alias: '', hidden: false },
      // settings subroutes
      access: { alias: '', hidden: false },
      share: { alias: '', hidden: false },
      datasets: { alias: '', hidden: false },
      customer_brands: { alias: '', hidden: false },
      system_prompt: { alias: '', hidden: false },
      run_scheduled_agents: { alias: '', hidden: false },
      profile: { alias: '', hidden: false },
      //
      catalog: { alias: '', hidden: false },
      pdp: { alias: '', hidden: false },
      custom_dashboards: { alias: '', hidden: false },
      product_watch: { alias: '', hidden: false },
    },
    terms: { post: '', sentence: '' },
  },
}

const detailsDefault = {
  state: '',
  title: '',
  description: '',
  proj_uuid: '',
  role: '',
  menu: {
    Dashboard: '',
    Feedback: '',
    Network: '',
    Sources: '',
    Products: '',
  },
  clientSettings: clientSettingsDefault,
}

export const initialState = {
  projectId: '',
  details: detailsDefault,
  isHyperCopy: false,
  dateWindows: {} as Record<string, DateRange[]>,
}

// =============================================================================
// Helper: reset all sub-stores in one call
// =============================================================================
function resetAllSubStores(): void {
  useChartsV2Store.getState().resetChartsV2()
  useFilterUIStore.getState().resetFilterUI()
  useChartStore.getState().resetChartStore()
  useSummaryUIStore.getState().resetSummaryUI()
  useAlertUIStore.getState().resetAlertUI()
  useSuggestedViewsUIStore.getState().resetSuggestedViewsUI()
  useAskYogiUIStore.getState().resetAskYogiUI()
  useFeedbackControlsStore.getState().resetFeedbackControls()
  useFeedbackQueryStore.getState().resetFeedbackQueries()
  useKeywordsControlsStore.getState().resetKeywordsControls()
  useKeywordsQueryStore.getState().resetKeywordsQueries()
  usePanelStore.getState().resetPanels()
  useFilterStore.getState().resetFilterState()
}

// =============================================================================
// Project store — core cross-cutting state.
// Feature-specific state lives in feature-local stores.
// =============================================================================

export const useProjectStore = create<ProjectState>()((set) => ({
  ...initialState,

  setProjectId: (projectId: string) =>
    set(
      produce((state: ProjectState) => {
        state.projectId = projectId
      }),
    ),

  setDetails: (details: ProjectDetails) =>
    set(
      produce((state: ProjectState) => {
        state.details = details
      }),
    ),

  setIsHyperCopy: (isHyperCopy: boolean) =>
    set(
      produce((state: ProjectState) => {
        state.isHyperCopy = isHyperCopy
      }),
    ),

  setDateWindows: (dateWindows: Record<string, DateRange[]>) =>
    set(
      produce((state: ProjectState) => {
        state.dateWindows = dateWindows
      }),
    ),

  reset: () => {
    set(initialState as unknown as Partial<ProjectState>)
    resetAllSubStores()
  },
}))
